import { Component, OnInit } from '@angular/core';
import { SliderStepComponent } from '../../step/slider-step/slider-step.component';
import { ControlContainer, FormGroupDirective, UntypedFormControl, Validators } from '@angular/forms';

@Component({
    selector: 'app-first-mortgage-balance',
    templateUrl: './first-mortgage-balance.component.html',
    styleUrls: ['./first-mortgage-balance.component.scss', '../../step/step.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    standalone: false
})
export class FirstMortgageBalanceComponent extends SliderStepComponent implements OnInit {
  public enterFieldValue: number | undefined;
  // FRU-24242 / FRU-24230: what the step shows for a $0 balance. The slider has no $0 stop; $0 is an answer only on purpose.
  protected readonly noMortgageLabel = 'No mortgage';
  public homeVal: number;
  public title: string = "Estimate Total Existing Mortgage Balance";
  public validationMessage = '';

  ngOnInit() {
    this.initializeData();

    this.control = new UntypedFormControl('', Validators.required);
    this.form.addControl('firstMortgageBalance', this.control);
  }

  /** FRU-24242 (won the FRU-24242-No-mortgage A/B test, now shown to everyone): the visitor has no mortgage. Submit a $0 balance, the same as choosing $0 on the slider. */
  noMortgage(): void {
    this.analyticsService.trackEvent('RatesForm', 'no_mortgage_balance_click', 'FRU-24242');
    this.analyticsService.trackGoal(144);   // FRU-24242: Matomo goal 144, "No mortgage" clicked
    this.countVisitorAction();
    // As a typed $0: Back to this step then shows "No mortgage" and 0 in the box (initStep), and Continue keeps it.
    this.enterFieldValue = 0;
    this.enterStringFieldValue = '0';
    this.positionSliderFor(0);
    this.submit(this.sliderValue);
  }

  initializeData () {
    this.sliderValues = [60000];
    this.sliderLabels = ['$60,000 and under'];
    
    this.sliderValue = 30;
    // FRU-24230 (as Hope): no $0 stop. It was the first stop, so dragging the slider all the way left sent a $0 balance and
    // lenders got the lead as a home owned outright; about a quarter of refinance-type leads went out at $0.
    this.sliderValues = [60000, 65000, 70000, 75000, 80000, 85000, 90000, 95000, 100000, 105000, 110000, 115000, 120000, 125000,
      130000, 135000, 140000, 145000, 150000, 155000, 160000, 165000, 170000, 175000, 180000, 185000, 190000, 195000, 200000, 210000,
      220000, 230000, 240000, 250000, 260000, 270000, 280000, 290000, 300000, 310000, 320000, 330000, 340000, 350000, 360000, 370000,
      380000, 390000, 400000, 420000, 440000, 460000, 480000, 500000, 520000, 540000, 560000, 580000, 600000, 620000, 640000, 660000,
      680000, 700000, 720000, 740000, 760000, 780000, 800000, 820000, 840000, 860000, 880000, 900000, 920000, 940000, 960000, 980000,
      1000000, 1100000, 1200000, 1300000];

    this.sliderLabels = ['$60,000 and under', '$60,001 - $65,000', '$65,001 - $70,000', '$70,001 - $75,000', '$75,001 - $80,000',
      '$80,001 - $85,000', '$85,001 - $90,000', '$90,001 - $95,000', '$95,000 - $100,000', '$100,001 - $105,000', '$105,001 - $110,000',
      '$110,001 - $115,000', '$115,001 - $120,000', '$120,001 - $125,000', '$125,001 - $130,000', '$130,001 - $135,000', '$135,001 - $140,000',
      '$140,001 - $145,000', '$145,001 - $150,000', '$150,001 - $155,000', '$155,001 - $160,000', '$160,001 - $165,000', '$165,001 - $170,000',
      '$170,001 - $175,000', '$175,001 - $180,000', '$180,001 - $185,000', '$185,001 - $190,000', '$190,001 - $195,000', '$195,001 - $200,000',
      '$200,001 - $210,000', '$210,001 - $220,000', '$220,001 - $230,000', '$230,001 - $240,000', '$240,001 - $250,000', '$250,001 - $260,000',
      '$260,001 - $270,000', '$270,001 - $280,000', '$280,001 - $290,000', '$290,001 - $300,000', '$300,001 - $310,000', '$310,001 - $320,000',
      '$320,001 - $330,000', '$330,001 - $340,000', '$340,001 - $350,000', '$350,001 - $360,000', '$360,001 - $370,000', '$370,001 - $380,000',
      '$380,001 - $390,000', '$390,001 - $400,000', '$400,001 - $420,000', '$420,001 - $440,000', '$440,001 - $460,000', '$460,001 - $480,000',
      '$480,001 - $500,000', '$500,001 - $520,000', '$520,001 - $540,000', '$540,001 - $560,000', '$560,001 - $580,000', '$580,001 - $600,000',
      '$600,001 - $620,000', '$620,001 - $640,000', '$640,001 - $660,000', '$660,001 - $680,000', '$680,001 - $700,000', '$700,001 - $720,000',
      '$720,001 - $740,000', '$740,001 - $760,000', '$760,001 - $780,000', '$780,001 - $800,000', '$800,001 - $820,000', '$820,001 - $840,000',
      '$840,001 - $860,000', '$860,001 - $880,000', '$880,001 - $900,000', '$900,001 - $920,000', '$920,001 - $940,000', '$940,001 - $960,000',
      '$960,001 - $980,000', '$980,001 - $1,000,000', '$1,000,001 - $1,100,000', '$1,100,001 - $1,200,000', 'Over $1,200,000'];

    //checking fo 25%(ab test) or 50%(original) of the mortgage balance
    this.sliderValue = this.findIndex(50);
    this.sliderDisplay = this.sliderLabels[this.sliderValue];
  }

  initStep() {
    this.homeVal = this.form.controls.propertyValue.value;
    this.initializeData();
    // Back to this step: the box keeps the amount the visitor submitted (typed, slid or "No mortgage"), and Continue sends
    // it again, but initializeData() put the slider back on its default. Show that amount on the slider too.
    if (typeof this.enterFieldValue === 'number' && this.control?.value === this.enterFieldValue) {
      this.positionSliderFor(this.enterFieldValue);
    }
  }

  slide(e) {
    this.sliderValue = e.target.value;
    this.sliderDisplay = this.sliderLabels[e.target.value];
    this.enterFieldValue = this.sliderValues[this.sliderValue];

    //add commas to numeric inputs
    this.enterStringFieldValue = this.enterFieldValue.toLocaleString();
  }

  setSlide(e) {
    this.keepTypedAmountText(e);   // digits and commas, written into the field now (as Hope's)
    let value = parseFloat(e.target.value.replace(/,/g, ''));
    if (isNaN(value)) {
      // FRU-24230: a cleared box is not a $0 balance (it used to be). Continue sends the slider's stop, so show its label
      // (a typed 0 before it showed "No mortgage").
      this.enterFieldValue = undefined;
      this.enterStringFieldValue = '';
      this.sliderDisplay = this.sliderLabels[this.sliderValue];
      return;
    }
    this.enterFieldValue = value;

    this.positionSliderFor(this.enterFieldValue);
  }

  /** The slider stop and label for an amount (typed, or submitted before Back). */
  private positionSliderFor(amount: number): void {
    if (amount === 0) {
      // "No mortgage" or a typed 0: the slider rests on its first stop, the label says what will be sent.
      this.sliderValue = 0;
      this.sliderDisplay = this.noMortgageLabel;
      return;
    }
    if (amount <= 200000) {
      var diff = 5000;
    } else if (amount <= 400000) {
      var diff = 10000;
    } else if (amount <= 1000000) {
      var diff = 20000;
    } else {
      var diff = 100000;
    }


    for(var i = 0; i < this.sliderValues.length; i++) {
      if ((this.sliderValues[i] - diff) < amount && (this.sliderValues[i + 1] == undefined || (this.sliderValues[i + 1] - diff) >= amount)) {
        this.sliderValue = i;
        break;
      }
    }

    if (amount <= 60000) {
      this.sliderValue = 0;
    }

    this.sliderDisplay = this.sliderLabels[this.sliderValue];
  }

  countVisitorAction() {
    this.form.controls.firstMortgageBalanceAction?.setValue('1');
  }

  submit(value: any): void {
    const submittedValue = typeof this.enterFieldValue === 'number' && !isNaN(this.enterFieldValue)
      ? this.enterFieldValue
      : (typeof value === 'undefined' ? this.sliderValues[this.sliderValue] : this.sliderValues[value]);

    if (!this.isBalanceValid(submittedValue)) { return; }

    this.control.setValue(submittedValue);
    this.setDesiredLoanAmount();
    this.setLTV();
    this.analyticsService.trackQuestionAnswered();
    this.nextStep();
  }

  private isBalanceValid(amount: number): boolean {
    const homeValue = Number(this.form.controls.propertyValue?.value || this.homeVal || 0);
    const balance = Number(amount || 0);
    const maxBalance = Math.max(...(this.sliderValues || [0]));
    if (maxBalance > 0 && balance > maxBalance) {
      this.validationMessage = 'Please enter a mortgage balance at or below $' + maxBalance.toLocaleString('en-US') + '.';
      this.control.setErrors({ greaterThanSliderMax: true });
      return false;
    }
    if (homeValue > 0 && balance > homeValue) {
      this.validationMessage = "Mortgage balance can't be higher than the home's value.";
      this.control.setErrors({ greaterThanHomeValue: true });
      return false;
    }
    this.validationMessage = '';
    this.control.setErrors(null);
    return true;
  }

  /**
   * find Index In SliderValues array By Percent Of the HomeValue
   * @param percent
   * @returns
   */
  private findIndex(percent:number) {
    const value = (this.homeVal/100)*percent;
    const closest = this.sliderValues.reduce((a, b) => {
      return Math.abs(b - value) < Math.abs(a - value) ? b : a;
    });

    return this.sliderValues.indexOf(closest);
  }
}
